import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from 'reactstrap';
import { KNOWN_SCOPES } from './fixture';
import { isApiError } from '../../api/errors';
import { useCreateApiKey } from './useApiKeys';
import { OneShotSecretReveal } from '../../components/OneShotSecretReveal';

/**
 * Zwei-Stufen-Flow:
 *  Stufe 1: Form (Label + Scope-Checkboxen + Expiry-Date)
 *  Stufe 2: OneShotSecretReveal mit dem Plaintext (genau einmal sichtbar)
 *
 * Nach Acknowledge → Modal komplett zu, Liste invalidiert.
 */
export function CreateApiKeyModal({ isOpen, onClose }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const create = useCreateApiKey();

  const reset = () => {
    setLabel('');
    setScopes([]);
    setExpiresAt('');
    setError(null);
    setCreated(null);
  };

  const onCancel = () => {
    if (created) return; // can't dismiss the secret modal via backdrop
    reset();
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!label.trim()) {
      setError('Label ist erforderlich.');
      return;
    }
    if (scopes.length === 0) {
      setError('Mindestens ein Scope auswählen.');
      return;
    }
    try {
      const result = await create.mutateAsync({
        label: label.trim(),
        scopes,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setCreated(result);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Anlegen fehlgeschlagen.');
    }
  };

  const onSecretAcknowledged = () => {
    reset();
    onClose();
  };

  const toggleScope = (s) => {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  return (
    <>
      <Modal isOpen={isOpen && !created} toggle={onCancel} backdrop="static">
        <ModalHeader toggle={onCancel}>Neuer API-Key</ModalHeader>
        <form onSubmit={onSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="apiKeyLabel">Label</Label>
              <Input
                id="apiKeyLabel"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="z. B. valiKey backend (prod)"
                maxLength={100}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Scopes</Label>
              <div className="d-flex flex-wrap gap-2">
                {KNOWN_SCOPES.map((s) => (
                  <FormGroup check inline key={s}>
                    <Input
                      type="checkbox"
                      id={`scope-${s}`}
                      checked={scopes.includes(s)}
                      onChange={() => toggleScope(s)}
                    />
                    <Label check for={`scope-${s}`} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {s}
                      {s === 'saas.sso.mint' && (
                        <span title="Operator-Mint — eigener Key empfohlen" className="text-warning ms-1">
                          ⚠
                        </span>
                      )}
                    </Label>
                  </FormGroup>
                ))}
              </div>
              <small className="text-muted">
                <code>saas.sso.mint</code> ist privilegierter Operator-Scope —
                immer in einen separaten Key isolieren.
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="apiKeyExpiry">Ablaufdatum <span className="text-muted">(optional)</span></Label>
              <Input
                type="date"
                id="apiKeyExpiry"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </FormGroup>

            {error && <Alert color="danger">{error}</Alert>}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={onCancel} disabled={create.isPending}>
              Abbrechen
            </Button>
            <Button color="primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Anlegen…' : 'Anlegen'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {created && (
        <OneShotSecretReveal
          isOpen
          label="API-Key Plaintext"
          secret={created.plaintext}
          confirmPhrase="Ich habe das Secret sicher hinterlegt und verstehe, dass es danach nicht mehr abrufbar ist"
          onAcknowledged={onSecretAcknowledged}
        />
      )}
    </>
  );
}
